'use client';

/**
 * ReplyComposer — manual reply composer for the CRM inbox (§9).
 *
 * Manual by default. The channel is CONTROLLED by the parent (the inbox syncs
 * it with the thread's visible history): chips sit in the bottom action bar,
 * and switching channel pins the reply to the account the thread already used on
 * that channel (§11: a thread stays on one account), so a reply always goes out
 * from the right inbox. There's no subject field — an inbox reply always continues
 * an existing conversation (the engine sent the opener), so it threads into that
 * conversation and reuses its subject server-side. Every channel composes in a
 * plain textarea; on send an email body is wrapped into simple HTML (the server
 * send path is HTML-aware) so line breaks survive, while LinkedIn/WhatsApp go out
 * as plain text. The body is resizable by dragging the grip above it (height
 * persists per browser).
 * Recruiters can insert a booking link or a canned template. Coral (`--accent`)
 * is reserved for Send.
 */

import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
    Send,
    CalendarPlus,
    FileText,
    ChevronDown,
    Sparkles,
    Loader2,
    AlertCircle,
    Plus,
} from 'lucide-react';
import { Button } from '@/components/ui-v2';
import {
    ReplyTemplatesModal,
    type ReplyTemplate,
    type ReplyTemplateDraft,
} from './ReplyTemplatesModal';
import type { ReplyAccounts, ReplyPayload, StepChannel } from '../types';
import { textToHtml } from '../rich-text';
import { getInboxIcon } from './inbox-icons';

function draftErrMsg(err: unknown): string {
    const e = err as { response?: { data?: { error?: string } }; message?: string };
    return e?.response?.data?.error || e?.message || 'Couldn’t draft a reply. Please try again.';
}

const CHANNEL_LABEL: Record<StepChannel, string> = { email: 'Email', linkedin: 'LinkedIn', whatsapp: 'WhatsApp' };

const DEFAULT_TEMPLATES: ReplyTemplate[] = [
    {
        id: 'quick-intro-follow-up',
        label: 'Quick intro follow-up',
        body: 'Hi {{firstName}}, just following up on my note below — would you be open to a quick chat this week?',
    },
    {
        id: 'polite-close',
        label: 'Polite close',
        body: 'Completely understand — I’ll close this out for now. If anything changes, feel free to reach back out. All the best!',
    },
];

const REPLY_TEMPLATES_KEY = 'ot.inbox.replyTemplates.v1';

function loadReplyTemplates(): ReplyTemplate[] {
    if (typeof window === 'undefined') return DEFAULT_TEMPLATES;
    try {
        const raw = window.localStorage.getItem(REPLY_TEMPLATES_KEY);
        if (!raw) return DEFAULT_TEMPLATES;
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return DEFAULT_TEMPLATES;
        return parsed.filter(
            (template): template is ReplyTemplate =>
                typeof template?.id === 'string' &&
                typeof template?.label === 'string' &&
                typeof template?.body === 'string' &&
                template.label.trim().length > 0 &&
                template.body.trim().length > 0
        );
    } catch {
        return DEFAULT_TEMPLATES;
    }
}

function newTemplateId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    return `reply-template-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function resolveTemplateBody(body: string, firstName?: string, bookingLink?: string): string {
    return body
        .replace(/\{\{\s*(?:candidate\.)?firstName\s*\}\}/gi, firstName?.trim() || 'there')
        .replace(/\{\{\s*(?:calendar\.link|bookingLink)\s*\}\}/gi, bookingLink?.trim() || '');
}

export interface ReplyComposerHandle {
    focus: () => void;
}

const BODY_HEIGHT_KEY = 'ot.inbox.composerHeight';
const BODY_HEIGHT_MIN = 64;
const BODY_HEIGHT_MAX = 480;

export interface ReplyComposerProps {
    channels: StepChannel[];
    /** Controlled: the channel this reply goes out on (parent syncs it with the
     *  thread's visible history). */
    channel: StepChannel;
    onChannelChange: (ch: StepChannel) => void;
    replyAccounts: ReplyAccounts;
    bookingLink?: string;
    candidateFirstName?: string;
    sending?: boolean;
    disabled?: boolean;
    /** Show the "Draft with AI" assist (hidden on escalated threads / no inbound). */
    canDraft?: boolean;
    /** Fetch an AI-suggested reply draft for the chosen channel (never sends). */
    onDraft?: (channel: StepChannel) => Promise<{ body: string }>;
    onSend: (payload: ReplyPayload) => Promise<void> | void;
}

export const ReplyComposer = forwardRef<ReplyComposerHandle, ReplyComposerProps>(
    (
        {
            channels,
            channel,
            onChannelChange,
            replyAccounts,
            bookingLink,
            candidateFirstName,
            sending,
            disabled,
            canDraft,
            onDraft,
            onSend,
        },
        ref
    ) => {
        const available: StepChannel[] = channels.length ? channels : ['email'];
        // Every channel composes as plain text in the textarea. On send, an email
        // body is wrapped into simple HTML (the send path is HTML-aware); the other
        // channels send the trimmed text as-is.
        const isEmail = channel === 'email';
        const [body, setBody] = useState('');
        const [showTemplates, setShowTemplates] = useState(false);
        const [templates, setTemplates] = useState<ReplyTemplate[]>(loadReplyTemplates);
        const [showTemplateManager, setShowTemplateManager] = useState(false);
        const [drafting, setDrafting] = useState(false);
        const [draftError, setDraftError] = useState<string | null>(null);
        const [draftedByAi, setDraftedByAi] = useState(false);
        const textareaRef = useRef<HTMLTextAreaElement>(null);

        // Resizable body — drag the grip above the composer; persists per browser.
        const [bodyHeight, setBodyHeight] = useState<number>(() => {
            if (typeof window === 'undefined') return 110;
            const saved = Number(window.localStorage.getItem(BODY_HEIGHT_KEY));
            return Number.isFinite(saved) && saved >= BODY_HEIGHT_MIN ? Math.min(saved, BODY_HEIGHT_MAX) : 110;
        });
        useEffect(() => {
            try {
                window.localStorage.setItem(BODY_HEIGHT_KEY, String(bodyHeight));
            } catch {
                /* private mode etc. — height just won't persist */
            }
        }, [bodyHeight]);

        useEffect(() => {
            try {
                window.localStorage.setItem(REPLY_TEMPLATES_KEY, JSON.stringify(templates));
            } catch {
                /* private mode etc. — templates remain available for this session */
            }
        }, [templates]);

        useImperativeHandle(ref, () => ({
            focus: () => textareaRef.current?.focus(),
        }));

        const account = replyAccounts[channel];
        const hasContent = body.trim().length > 0;
        const canSend = hasContent && !sending && !disabled;

        const insert = (snippet: string) => {
            setBody((b) => (b.trim() ? `${b.trimEnd()}\n\n${snippet}` : snippet));
            textareaRef.current?.focus();
            setShowTemplates(false);
        };

        const openTemplateManager = () => {
            setShowTemplates(false);
            setShowTemplateManager(true);
        };

        const deleteTemplate = (templateId: string) => {
            setTemplates((current) => current.filter((template) => template.id !== templateId));
        };

        const saveTemplate = (template: ReplyTemplateDraft) => {
            if (template.id) {
                setTemplates((current) =>
                    current.map((currentTemplate) =>
                        currentTemplate.id === template.id ? { ...currentTemplate, ...template } : currentTemplate
                    )
                );
            } else {
                setTemplates((current) => [...current, { ...template, id: newTemplateId() }]);
            }
        };

        const handleDraft = async () => {
            if (!onDraft || drafting || sending) return;
            setDrafting(true);
            setDraftError(null);
            try {
                const draft = await onDraft(channel);
                // The AI draft comes back as plain text — drop it straight in.
                setBody(draft.body);
                textareaRef.current?.focus();
                setDraftedByAi(true);
            } catch (err) {
                setDraftError(draftErrMsg(err));
            } finally {
                setDrafting(false);
            }
        };

        const handleSend = async () => {
            if (!canSend) return;
            // No subject: an inbox reply always continues an existing thread, so the
            // server reuses the conversation's subject and threads the message (§9).
            // Email goes out as simple HTML (the send path is HTML-aware) so line
            // breaks survive; plain-text channels send trimmed text.
            const trimmed = body.trim();
            const payload: ReplyPayload = { channel, body: isEmail ? textToHtml(trimmed) : trimmed };
            try {
                await onSend(payload);
                setBody('');
                setDraftedByAi(false);
            } catch {
                // Send failed — the parent surfaces the error. Keep the typed text
                // so the recruiter can retry instead of losing their message.
            }
        };

        return (
            <div
                className="flex flex-col"
                style={{ borderTop: '1px solid var(--border-default)', background: 'var(--surface-primary)' }}
            >
                {/* Resize grip — drag up to grow the draft area */}
                <div
                    role="separator"
                    aria-orientation="horizontal"
                    aria-label="Resize the reply box"
                    className="group flex h-3 w-full shrink-0 cursor-row-resize items-center justify-center"
                    onPointerDown={(e) => {
                        e.preventDefault();
                        const el = e.currentTarget;
                        try {
                            el.setPointerCapture(e.pointerId);
                        } catch {
                            /* capture is an enhancement — the drag still tracks the strip */
                        }
                        let lastY = e.clientY;
                        const move = (ev: PointerEvent) => {
                            const dy = lastY - ev.clientY; // dragging up = taller
                            lastY = ev.clientY;
                            setBodyHeight((h) => Math.min(BODY_HEIGHT_MAX, Math.max(BODY_HEIGHT_MIN, h + dy)));
                        };
                        const up = () => {
                            el.removeEventListener('pointermove', move);
                            el.removeEventListener('pointerup', up);
                        };
                        el.addEventListener('pointermove', move);
                        el.addEventListener('pointerup', up);
                    }}
                >
                    <div
                        className="h-[3px] w-10 rounded-full transition-colors"
                        style={{ background: 'var(--border-default)' }}
                    />
                </div>

                {/* Body — no subject field: an inbox reply always threads into the
                    existing conversation and reuses its subject server-side (§9).
                    One plain textarea for every channel; an email body is wrapped to
                    HTML on send. Cmd/Ctrl+Enter sends. */}
                <textarea
                    ref={textareaRef}
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    onKeyDown={(e) => {
                        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                            e.preventDefault();
                            handleSend();
                        }
                    }}
                    placeholder={`Write a reply on ${CHANNEL_LABEL[channel]}…`}
                    aria-label={`${CHANNEL_LABEL[channel]} reply body`}
                    className="mx-4 mt-2 resize-none rounded-lg px-3 py-2.5 text-[13px] leading-relaxed outline-none"
                    style={{
                        height: bodyHeight,
                        border: '1px solid var(--border-default)',
                        background: 'var(--surface-primary)',
                        color: 'var(--text-primary)',
                    }}
                />

                {/* AI-draft status line — appears after a draft is inserted or fails */}
                {(draftedByAi || draftError) && (
                    <div className="mx-4 mt-2 flex items-center gap-1.5 text-[11.5px]">
                        {draftError ? (
                            <span className="flex items-center gap-1.5" style={{ color: 'var(--fg-danger)' }}>
                                <AlertCircle size={12} strokeWidth={2} /> {draftError}
                            </span>
                        ) : (
                            <span className="flex items-center gap-1.5" style={{ color: 'var(--text-tertiary)' }}>
                                <Sparkles size={12} strokeWidth={1.75} style={{ color: 'var(--accent)' }} />
                                AI draft — review and edit before sending.
                            </span>
                        )}
                    </div>
                )}

                {/* Action bar — channel choice lives down here, next to send tools */}
                <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
                    <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                        {/* Channel selector — switching adapts the composer (no subject
                            off email) and the thread view; pinned per-channel account (§11). */}
                        <div className="flex items-center gap-1">
                            {available.map((ch) => {
                                const Icon = getInboxIcon(ch, replyAccounts[ch]?.provider);
                                const active = channel === ch;
                                return (
                                    <button
                                        key={ch}
                                        onClick={() => onChannelChange(ch)}
                                        className="flex h-7 items-center gap-1.5 rounded-lg px-2.5 text-[12px] font-medium transition-colors"
                                        style={{
                                            background: active ? 'var(--accent-muted)' : 'transparent',
                                            color: active ? 'var(--accent-emphasis)' : 'var(--text-secondary)',
                                            border: `1px solid ${active ? 'var(--border-accent)' : 'var(--border-default)'}`,
                                        }}
                                        title={
                                            available.length > 1
                                                ? `Reply on ${CHANNEL_LABEL[ch]}`
                                                : `This thread is on ${CHANNEL_LABEL[ch]}`
                                        }
                                        aria-pressed={active}
                                    >
                                        <Icon size={12} strokeWidth={2} /> {CHANNEL_LABEL[ch]}
                                    </button>
                                );
                            })}
                        </div>
                        {account && (
                            <span
                                className="max-w-[160px] truncate text-[11px]"
                                style={{ color: 'var(--text-tertiary)' }}
                                title={`Sends from ${account.displayName || account.emailAddress || 'the connected account'}`}
                            >
                                from {account.displayName || account.emailAddress || 'connected account'}
                            </span>
                        )}
                        <span className="h-4 w-px shrink-0" style={{ background: 'var(--border-muted)' }} />

                        {/* AI reply assist — drafts a reply to the candidate's last message
                            (hidden on escalated threads, where a human writes every reply). */}
                        {canDraft && onDraft && (
                            <button
                                onClick={handleDraft}
                                disabled={drafting || sending || disabled}
                                className="flex h-7 items-center gap-1.5 rounded-lg px-2 text-[12px] font-medium transition-colors disabled:opacity-50"
                                style={{ color: 'var(--accent-emphasis)' }}
                                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--accent-muted)')}
                                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                                title="Draft a reply with AI"
                            >
                                {drafting ? (
                                    <Loader2 size={13} strokeWidth={2} className="animate-spin" />
                                ) : (
                                    <Sparkles size={13} strokeWidth={1.75} />
                                )}
                                {drafting ? 'Drafting…' : 'Draft with AI'}
                            </button>
                        )}

                        {/* Manual replies go out verbatim — never offer an unresolvable
                            {{calendar.link}} token; show the button only with a real URL. */}
                        {bookingLink && (
                            <button
                                onClick={() => insert(bookingLink)}
                                className="flex h-7 items-center gap-1.5 rounded-lg px-2 text-[12px] font-medium transition-colors"
                                style={{ color: 'var(--text-secondary)' }}
                                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--surface-hover)')}
                                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                                title="Insert booking link"
                            >
                                <CalendarPlus size={13} strokeWidth={1.75} /> Booking link
                            </button>
                        )}

                        <div className="relative">
                            <button
                                onClick={() => setShowTemplates((s) => !s)}
                                className="flex h-7 items-center gap-1.5 rounded-lg px-2 text-[12px] font-medium transition-colors"
                                style={{ color: 'var(--text-secondary)' }}
                                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--surface-hover)')}
                                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                            >
                                <FileText size={13} strokeWidth={1.75} /> Template
                                <ChevronDown size={12} strokeWidth={2} />
                            </button>
                            {showTemplates && (
                                <div
                                    className="absolute bottom-9 left-0 z-10 w-64 overflow-hidden rounded-lg"
                                    style={{
                                        background: 'var(--surface-primary)',
                                        border: '1px solid var(--border-default)',
                                        boxShadow: 'var(--shadow-lg)',
                                    }}
                                >
                                    {templates.length === 0 && (
                                        <div
                                            className="px-3 py-2.5 text-[12px]"
                                            style={{ color: 'var(--text-tertiary)' }}
                                        >
                                            No saved templates yet.
                                        </div>
                                    )}
                                    {templates.map((t, i) => (
                                        <button
                                            key={t.id}
                                            onClick={() =>
                                                insert(resolveTemplateBody(t.body, candidateFirstName, bookingLink))
                                            }
                                            className="w-full px-3 py-2 text-left text-[12px] transition-colors"
                                            style={{
                                                color: 'var(--text-primary)',
                                                borderTop: i === 0 ? 'none' : '1px solid var(--border-muted)',
                                            }}
                                            onMouseEnter={(e) =>
                                                (e.currentTarget.style.background = 'var(--surface-hover)')
                                            }
                                            onMouseLeave={(e) =>
                                                (e.currentTarget.style.background = 'transparent')
                                            }
                                        >
                                            {t.label}
                                        </button>
                                    ))}
                                    <button
                                        type="button"
                                        onClick={openTemplateManager}
                                        className="flex w-full items-center gap-1.5 px-3 py-2 text-left text-[12px] font-medium transition-colors"
                                        style={{
                                            color: 'var(--accent-emphasis)',
                                            borderTop: '1px solid var(--border-default)',
                                        }}
                                        onMouseEnter={(e) =>
                                            (e.currentTarget.style.background = 'var(--surface-hover)')
                                        }
                                        onMouseLeave={(e) =>
                                            (e.currentTarget.style.background = 'transparent')
                                        }
                                    >
                                        <Plus size={13} strokeWidth={2} /> Manage templates
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    <Button
                        variant="primary"
                        size="md"
                        disabled={!canSend}
                        onClick={handleSend}
                    >
                        <Send size={14} strokeWidth={2} /> {sending ? 'Sending…' : 'Send'}
                    </Button>
                </div>

                <ReplyTemplatesModal
                    open={showTemplateManager}
                    templates={templates}
                    onSave={saveTemplate}
                    onDelete={deleteTemplate}
                    onClose={() => setShowTemplateManager(false)}
                />
            </div>
        );
    }
);
ReplyComposer.displayName = 'ReplyComposer';
